import React, { useState } from 'react';
import AgentConversationHistory from './AgentConversationHistory';
import { deleteConversationBySessionId, getConversationBySessionId, getConversationHistory } from '../helpers-goPilot';
import { ConversationSessionDocument, ConversationSummary } from '../goFormz-agent-types';
import { Loader } from 'semantic-ui-react';
import ConversationIcon from '../../icons/ConversationIcon';
import CloseConversationIcon from '../../icons/CloseConversationIcon';
import NewConversationIcon from '../../icons/NewConversationIcon';
import PanelButton from '../../components/PanelButton';
import ConversationHistoryIcon from '../../icons/ConversationHistoryIcon';
import AgentDockModeDropdown from './AgentDockModeDropdown';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { useGoFormzAgent } from '../GoFormzAgentHook';

export default function AgentPopupHeader({
  onClose,
  onClear,
  historyToggled,
  historicalConversationSelected
}: {
  onClose: () => void;
  onClear: () => void;
  historyToggled: (showing: boolean) => void;
  historicalConversationSelected: (conversation: ConversationSessionDocument) => void;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const { dockPosition, dockMode, setDockPosition, setDockMode } = useGoFormzAgent();

  const sendAnalyticsEvent = (eventName: string, properties: Record<string, unknown>) => {
    try {
      analyticsTrack(eventName, properties);
    } catch (error) {
      //do nothing if analytics fails
    }
  };

  const showHistory = async () => {
    setLoading(true);
    historyToggled(true);
    const results = await getConversationHistory();
    setLoading(false);
    setHistory(results.history);
    setHistoryOpen(true);

    sendAnalyticsEvent('GoPilot - History Clicked', {
      category: 'GoPilotEvent'
    });
  };

  const loadConversationHistoryFromSummary = async (conversation: ConversationSummary) => {
    setHistoryOpen(false);
    const response = await getConversationBySessionId(conversation.conversationSessionId);
    if (response && response.messages) {
      historicalConversationSelected(response);
    }
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    historyToggled(false);
  };

  const clear = () => {
    setHistoryOpen(false);
    historyToggled(false);
    onClear();

    sendAnalyticsEvent('GoPilot - New Message Clicked', {
      category: 'GoPilotEvent'
    });
  };

  const deleteConversation = async (conversation: ConversationSummary) => {
    setLoading(true);
    setHistoryOpen(false);
    await deleteConversationBySessionId(conversation.conversationSessionId);
    const updatedHistory = history.filter((item) => item.conversationSessionId !== conversation.conversationSessionId);
    setHistory(updatedHistory);
    setHistoryOpen(true);
    setLoading(false);
  };

  return (
    <>
      <div className="ai-popup-header">
        <div className="ai-popup-header-buttons-row">
          {historyOpen && (
            <PanelButton onClick={closeHistory} aria-label="Close">
              <ConversationIcon />
            </PanelButton>
          )}
          {!historyOpen && (
            <PanelButton onClick={showHistory} aria-label="Show history">
              <ConversationHistoryIcon />
            </PanelButton>
          )}

          <div className="ai-popup-header-right-buttons">
            {/* Dock Position and Mode Dropdown */}
            <AgentDockModeDropdown dockPosition={dockPosition} dockMode={dockMode} onDockPositionChange={setDockPosition} onDockModeChange={setDockMode} />
            <PanelButton onClick={clear} aria-label="New conversation">
              <NewConversationIcon />
            </PanelButton>
            <PanelButton onClick={onClose} aria-label="Close">
              <CloseConversationIcon />
            </PanelButton>
          </div>
        </div>

        <div className="ai-popup-header-top">
          <span className="ai-beta-label">BETA – GoPilot</span>
        </div>
        {!historyOpen && <h2 className="ai-popup-title">Hello. How can I help you?</h2>}
        {historyOpen && <h2 className="ai-popup-title">Recent Conversations</h2>}
      </div>
      {historyOpen && (
        <div className="ai-popup-history">
          <AgentConversationHistory onDelete={deleteConversation} conversations={history} summarySelected={loadConversationHistoryFromSummary} />
        </div>
      )}
      {loading && (
        <div style={{ padding: '32px' }}>
          <Loader active inline="centered" size="small"></Loader>
        </div>
      )}
    </>
  );
}
